import { ComponentRef, DestroyRef, Directive, effect, inject, input, ViewContainerRef } from '@angular/core';

type LazyComponentFactory = (() => Promise<unknown>) | undefined;
interface LazyConfig {
  component?: LazyComponentFactory;
}
type OutputHandler = (value: unknown) => void;

@Directive({
  selector: '[appLazyDirective]'
})
export class LazyDirective {
  private readonly container = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly appLazyDirective = input<LazyConfig>();
  readonly inputs = input<Record<string, unknown> | null | undefined>();
  readonly outputs = input<Record<string, OutputHandler> | null | undefined>();

  private componentRef?: ComponentRef<object>;
  private outputSubscriptions: Array<{ unsubscribe: () => void }> = [];
  private loadVersion = 0;

  constructor() {
    effect(() => {
      void this.load();
    });

    effect(() => {
      const inputs = this.inputs();
      this.setInputs(inputs);
    });

    effect(() => {
      const outputs = this.outputs();
      this.bindOutputs(outputs);
    });

    this.destroyRef.onDestroy(() => {
      this.destroyComponent();
    });
  }

  async load() {
    const currentLoadVersion = ++this.loadVersion;
    const componentFactory = this.appLazyDirective()?.component;
    if (!componentFactory) {
      this.destroyComponent();
      return;
    }

    const componentModule = await componentFactory();
    if (currentLoadVersion !== this.loadVersion) {
      return;
    }

    const componentType = Object.values(componentModule as Record<string, unknown>)[0];
    if (!componentType) {
      this.destroyComponent();
      return;
    }

    this.destroyComponent();
    this.componentRef = this.container.createComponent(componentType as any);
    this.setInputs(this.inputs());
    this.bindOutputs(this.outputs());
  }

  setInputs(inputs: Record<string, unknown> | null | undefined) {
    if (!this.componentRef) return;
    if (!inputs || typeof inputs !== 'object') return;

    Object.entries(inputs).forEach(([key, value]) => {
      this.componentRef?.setInput(key, value);
    });

    this.componentRef.changeDetectorRef.detectChanges();
  }

  bindOutputs(outputs: Record<string, OutputHandler> | null | undefined) {
    if (!this.componentRef) return;

    this.clearOutputSubscriptions();
    if (!outputs || typeof outputs !== 'object') return;

    const instance = this.componentRef.instance as Record<string, { subscribe?: (handler: OutputHandler) => { unsubscribe: () => void } }>;
    Object.entries(outputs).forEach(([key, handler]) => {
      if (typeof handler !== 'function') return;
      const outputRef = instance[key];
      if (!outputRef?.subscribe) return;

      const subscription = outputRef.subscribe((value: unknown) => {
        handler(value);
      });
      this.outputSubscriptions.push(subscription);
    });
  }

  private clearOutputSubscriptions() {
    this.outputSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.outputSubscriptions = [];
  }

  private destroyComponent() {
    this.clearOutputSubscriptions();
    this.componentRef?.destroy();
    this.componentRef = undefined;
    this.container.clear();
  }

}
